// What a ticket looks like the instant an action is clicked, before the n8n
// dispatcher has answered.
//
// The dispatcher (n8n/workflows/_node-js/dispatcher-apply-action.js) stays the
// single source of truth (CLAUDE.md §5): this file only PREDICTS the fields it
// is about to write so the screen can move straight away. The server's real
// row replaces the prediction when it replies, and a refusal puts the old
// values back (see ticket-actions.ts).
//
// Pure on purpose (no React, no Supabase): tests/ticket-optimistic.test.ts
// imports it directly. If the dispatcher changes what an action writes, change
// the matching branch here and in that test.

/** The ticket columns an action can change. Structural, so the full `Ticket`
 *  row and a search-result ticket both satisfy it. */
export interface PatchableTicket {
  status?: string | null;
  priority?: string | null;
  assignee_slack_id?: string | null;
  snoozed_until?: string | null;
  archived_at?: string | null;
  resolved_at?: string | null;
  merged_into_ticket_id?: string | null;
  /** Robot mail flag (migration 080). Only read to decide whether a claim / reassign
   *  takes the ticket out of the FYI lane. */
  is_automated_sender?: boolean | null;
}

export type TicketFieldPatch = Partial<PatchableTicket>;

export interface PatchContext {
  /** The signed-in agent's slack_user_id (what `claim` assigns to). */
  actorSlackId: string | null;
  now?: Date;
}

const PRIORITIES = ['P1', 'P2', 'P3', 'P4'];

const SNOOZE_ACTIONS = new Set([
  'snooze_1h',
  'snooze_4h',
  'snooze_tomorrow',
  'snooze_monday',
  'snooze_2d',
  'snooze_custom',
]);

/**
 * The fields `action` is about to change, or `null` when the result can't be
 * predicted (unknown action, missing payload the server would reject, an
 * actor we don't know). `null` means "show nothing early", never "guess".
 */
export function optimisticTicketPatch(
  ticket: PatchableTicket,
  action: string,
  payload: Record<string, unknown> | undefined,
  ctx: PatchContext,
): TicketFieldPatch | null {
  const now = ctx.now ?? new Date();
  const nowIso = now.toISOString();
  const p = payload ?? {};

  switch (action) {
    case 'claim': {
      if (!ctx.actorSlackId) return null;
      // The dispatcher only moves a NEW ticket to in_progress; any other
      // status is written back unchanged.
      const claimed: TicketFieldPatch =
        ticket.status === 'new'
          ? { assignee_slack_id: ctx.actorSlackId, status: 'in_progress' }
          : { assignee_slack_id: ctx.actorSlackId };
      // A person taking a robot-mail ticket on makes it a working ticket (the
      // server stops treating it as robot mail once a teammate has claimed it).
      return ticket.is_automated_sender === true ? { ...claimed, is_automated_sender: false } : claimed;
    }
    case 'reassign': {
      // The key must be present; null / '' means "unassign".
      if (!Object.prototype.hasOwnProperty.call(p, 'reassign_to')) return null;
      const to = p.reassign_to;
      const reassigned: TicketFieldPatch = { assignee_slack_id: typeof to === 'string' && to ? to : null };
      return ticket.is_automated_sender === true ? { ...reassigned, is_automated_sender: false } : reassigned;
    }
    case 'resolve':
      return { status: 'resolved', resolved_at: nowIso };
    case 'close':
      return { status: 'closed', resolved_at: nowIso, archived_at: nowIso };
    case 'archive':
      return { archived_at: nowIso };
    case 'unarchive':
      return { archived_at: null };
    case 'escalate': {
      const m = /^P([1-4])$/.exec(ticket.priority ?? '');
      if (!m) return null;
      const n = Number(m[1]);
      return { priority: 'P' + (n <= 1 ? 1 : n - 1) };
    }
    case 'set_priority':
      return typeof p.priority === 'string' && PRIORITIES.includes(p.priority)
        ? { priority: p.priority }
        : null;
    case 'unsnooze':
      return { snoozed_until: null };
    case 'reopen':
      return {
        status: typeof p.status === 'string' && p.status ? p.status : 'in_progress',
        resolved_at: null,
        archived_at: null,
      };
    case 'merge': {
      // Describes the SOURCE ticket (the one that gets closed).
      const target = p.merge_into_ticket_id;
      if (typeof target !== 'string' || !target) return null;
      return {
        status: 'closed',
        resolved_at: nowIso,
        archived_at: nowIso,
        merged_into_ticket_id: target,
      };
    }
    default: {
      if (SNOOZE_ACTIONS.has(action)) {
        const until = snoozeUntil(action, p.custom_until, now);
        return until ? { snoozed_until: until } : null;
      }
      return null; // add_note, add_watcher, anything unknown
    }
  }
}

/**
 * Put back what an action changed, without undoing anything newer.
 *
 * Returns the fields to write: each field the optimistic patch set that STILL
 * holds the optimistic value goes back to `before`. A field something else has
 * changed since (another action, a refetch) is left alone — restoring a whole
 * snapshot would wipe that newer change.
 */
export function revertTicketPatch(
  current: PatchableTicket,
  applied: TicketFieldPatch,
  before: PatchableTicket,
): TicketFieldPatch {
  // Fields are strings or (the robot-mail flag) a boolean, so write through a loose record.
  const restore: Record<string, string | boolean | null> = {};
  for (const key of Object.keys(applied) as Array<keyof PatchableTicket>) {
    if ((current[key] ?? null) === (applied[key] ?? null)) {
      restore[key] = before[key] ?? null;
    }
  }
  return restore as TicketFieldPatch;
}

// ─── Snooze maths ────────────────────────────────────────────────────────────
// A port of computeSnoozeUntil / nextSydneyHour / nextSydneyMonday /
// sydneyWallClockToIso in the dispatcher. Every preset resolves to a wall-clock
// point in Australia/Sydney ("tomorrow" = 9am Sydney, not 9am UTC). Keep the
// two in step; the server's value replaces this one when it replies, so a
// drift would only show as the time changing by a moment.

/** `snoozed_until` for a snooze action, or null when it can't be computed. */
export function snoozeUntil(action: string, customUntil: unknown, now: Date): string | null {
  if (action === 'snooze_1h') return new Date(now.getTime() + 1 * 3600 * 1000).toISOString();
  if (action === 'snooze_4h') return new Date(now.getTime() + 4 * 3600 * 1000).toISOString();
  if (action === 'snooze_2d') return new Date(now.getTime() + 48 * 3600 * 1000).toISOString();
  if (action === 'snooze_custom') {
    if (typeof customUntil !== 'string') return null;
    const d = new Date(customUntil);
    if (isNaN(d.getTime()) || d.getTime() <= now.getTime()) return null;
    return d.toISOString();
  }
  if (action === 'snooze_tomorrow') return nextSydneyHour(now, 9, true);
  if (action === 'snooze_monday') return nextSydneyMonday(now, 9);
  return null;
}

interface SydneyParts {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
  weekday: string;
}

const sydneyFormat = new Intl.DateTimeFormat('en-AU', {
  timeZone: 'Australia/Sydney',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  weekday: 'short',
});

function sydneyParts(at: Date): SydneyParts {
  const parts = sydneyFormat.formatToParts(at);
  const get = (type: string) => parts.find((x) => x.type === type)?.value ?? '';
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
    weekday: get('weekday'),
  };
}

/** A Sydney wall-clock moment as a UTC ISO string. Tries AEST (UTC+10) then
 *  AEDT (UTC+11) and keeps whichever round-trips to the same wall clock. */
function sydneyWallClockToIso(year: number, month: number, day: number, hour: number, minute: number): string {
  for (const offsetHours of [10, 11]) {
    const candidate = new Date(Date.UTC(year, month - 1, day, hour - offsetHours, minute));
    const p = sydneyParts(candidate);
    if (
      parseInt(p.year, 10) === year &&
      parseInt(p.month, 10) === month &&
      parseInt(p.day, 10) === day &&
      parseInt(p.hour, 10) === hour
    ) {
      return candidate.toISOString();
    }
  }
  return new Date(Date.UTC(year, month - 1, day, hour - 11, minute)).toISOString();
}

/** Next time it is `targetHour:00` in Sydney. With `sameDayOk`, today counts
 *  while it is still before that hour. */
function nextSydneyHour(now: Date, targetHour: number, sameDayOk: boolean): string {
  const parts = sydneyParts(now);
  let day = parseInt(parts.day, 10);
  let month = parseInt(parts.month, 10);
  let year = parseInt(parts.year, 10);
  const hour = parseInt(parts.hour, 10);
  if (!(sameDayOk && hour < targetHour)) {
    const d = new Date(Date.UTC(year, month - 1, day + 1));
    day = d.getUTCDate();
    month = d.getUTCMonth() + 1;
    year = d.getUTCFullYear();
  }
  return sydneyWallClockToIso(year, month, day, targetHour, 0);
}

/** Next Monday at `targetHour:00` in Sydney; today if it is Monday and still
 *  before that hour. */
function nextSydneyMonday(now: Date, targetHour: number): string {
  const parts = sydneyParts(now);
  const day = parseInt(parts.day, 10);
  const month = parseInt(parts.month, 10);
  const year = parseInt(parts.year, 10);
  const hour = parseInt(parts.hour, 10);
  const daysFrom: Record<string, number> = { Mon: 0, Tue: 6, Wed: 5, Thu: 4, Fri: 3, Sat: 2, Sun: 1 };
  const offset =
    parts.weekday === 'Mon' && hour >= targetHour ? 7 : (daysFrom[parts.weekday] ?? 0);
  const target = new Date(Date.UTC(year, month - 1, day + offset));
  return sydneyWallClockToIso(
    target.getUTCFullYear(),
    target.getUTCMonth() + 1,
    target.getUTCDate(),
    targetHour,
    0,
  );
}
